import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Popover,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AiAnalyticsAPI from 'api/ai-analytics.api';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import FeedbackWidget from 'components/FeedbackWidget';
import CustomDatePicker from 'components/DatePIcker/CustomDatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PeriodData {
  hours: string[];
  total_production_tons: number;
  total_cycles: number;
  avg_tons_per_hour: number;
  avg_cycles_per_hour: number;
}

interface HaulTruckData {
  device: string;
  bumper_id: string;
  make: string;
  model: string;
  asset_type: string;
  daily_total: { production_tons: number; cycle_count: number };
  early_period: PeriodData;
  late_period: PeriodData;
  decline_pct: number;
  verdict: 'slacking' | 'overperforming' | 'normal';
  narrative: string;
  flags: string[];
}

interface LastHoursData {
  date: string;
  last_hours_analyzed: number;
  timeZone: string;
  fleet_summary: { haul_trucks_summary: string };
  haul_trucks: HaulTruckData[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DIAG_STEPS = [
  'Gathering shift telemetry…',
  'Analysing haul truck cycle rates…',
  'Calculating early-period baselines…',
  'Evaluating last-hour performance…',
  'Detecting queue avoidance patterns…',
  'Benchmarking end-of-shift behavior…',
  'Identifying slowdown trends…',
  'Ranking asset performance…',
  'Compiling shift verdicts…',
  'Generating insights…',
];

const LOADING_INSIGHTS = [
  {
    severity: 'info' as const,
    stat: '4 hrs',
    headline: 'End-of-Shift Analysis',
    detail:
      'Comparing final 4 hours against early shift baseline to identify performance drops.',
  },
  {
    severity: 'warning' as const,
    stat: '~40%',
    headline: 'Typical Shift-End Drop',
    detail:
      'Most haul trucks show a significant reduction in t/hr as shifts conclude — queue avoidance is a common pattern.',
  },
  {
    severity: 'info' as const,
    stat: '5 units',
    headline: 'Fleet Under Review',
    detail:
      'Analysing all active haul trucks across early and late shift windows.',
  },
];

const SEVERITY_COLORS = {
  critical: '#E24B4A',
  warning: '#EF9F27',
  info: '#378ADD',
};

const MAX_TPH = 145;

function getVerdictStyle(verdict: string, dark: boolean) {
  if (verdict === 'slacking')
    return {
      statusBg: dark ? 'rgba(226,75,74,0.18)' : '#FCEBEB',
      statusColor: dark ? '#F08080' : '#A32D2D',
      barColor: '#E24B4A',
    };
  if (verdict === 'overperforming')
    return {
      statusBg: dark ? 'rgba(29,158,117,0.18)' : '#EAF3DE',
      statusColor: dark ? '#4DB88A' : '#3B6D11',
      barColor: '#1D9E75',
    };
  return {
    statusBg: dark ? 'rgba(136,135,128,0.18)' : '#F1EFE8',
    statusColor: dark ? '#AAAAAA' : '#5F5E5A',
    barColor: '#888780',
  };
}

// ─── Loading Screen ───────────────────────────────────────────────────────────

function LoadingScreen({ enableDarkTheme }: { enableDarkTheme: boolean }) {
  const { t } = useTranslation();
  const [diagStep, setDiagStep] = useState(0);
  const [diagVisible, setDiagVisible] = useState(true);
  const [insightIndex, setInsightIndex] = useState(0);
  const [insightVisible, setInsightVisible] = useState(true);

  useEffect(() => {
    const iv = setInterval(() => {
      setDiagVisible(false);
      setTimeout(() => {
        setDiagStep((s) => (s + 1) % DIAG_STEPS.length);
        setDiagVisible(true);
      }, 300);
    }, 1800);
    return () => {
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setInsightVisible(false);
      setTimeout(() => {
        setInsightIndex((i) => (i + 1) % LOADING_INSIGHTS.length);
        setInsightVisible(true);
      }, 400);
    }, 3000);
    return () => {
      clearInterval(iv);
    };
  }, []);

  const insight = LOADING_INSIGHTS[insightIndex];
  const severityColor = SEVERITY_COLORS[insight.severity];

  const bg = enableDarkTheme ? '#0d1f35' : '#F8FAFB';
  const surface = enableDarkTheme ? '#111c2d' : '#ffffff';
  const textPrimary = enableDarkTheme ? '#e8ecf4' : '#1a2035';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#757575';
  const accent = '#5E85F0';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '580px',
        background: bg,
        gap: '32px',
        px: '60px',
        py: '48px',
      }}
    >
      {/* Radar scanner */}
      <Box sx={{ position: 'relative', width: 220, height: 220 }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px dashed ${accent}55`,
            '@keyframes lha-spin-slow': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            animation: 'lha-spin-slow 8s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: '18px',
            borderRadius: '50%',
            border: `1.5px solid ${accent}44`,
            '@keyframes lha-ring-pulse': {
              '0%,100%': { opacity: 0.4, transform: 'scale(1)' },
              '50%': { opacity: 1, transform: 'scale(1.04)' },
            },
            animation: 'lha-ring-pulse 2.4s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: '38px',
            borderRadius: '50%',
            border: `1px solid ${accent}33`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `conic-gradient(from 0deg, transparent 300deg, ${accent}55 360deg)`,
            '@keyframes lha-radar-spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            animation: 'lha-radar-spin 2s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '10%',
            right: '10%',
            height: '1px',
            background: `${accent}33`,
            transform: 'translateY(-50%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '10%',
            bottom: '10%',
            width: '1px',
            background: `${accent}33`,
            transform: 'translateX(-50%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: accent,
            transform: 'translate(-50%, -50%)',
            '@keyframes lha-dot-pulse': {
              '0%,100%': { boxShadow: `0 0 0 0 ${accent}88` },
              '50%': { boxShadow: `0 0 0 8px ${accent}00` },
            },
            animation: 'lha-dot-pulse 1.6s ease-in-out infinite',
          }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: accent,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          transition: 'opacity 0.3s ease',
          opacity: diagVisible ? 1 : 0,
          minHeight: '18px',
          textAlign: 'center',
        }}
      >
        {DIAG_STEPS[diagStep]}
      </Typography>

      <Box
        sx={{
          width: '100%',
          maxWidth: '560px',
          borderRadius: '10px',
          overflow: 'hidden',
          background: surface,
          boxShadow: enableDarkTheme
            ? '0 2px 16px rgba(0,0,0,0.45)'
            : '0 2px 16px rgba(0,0,0,0.08)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          opacity: insightVisible ? 1 : 0,
          transform: insightVisible ? 'translateY(0)' : 'translateY(6px)',
        }}
      >
        <Box sx={{ background: severityColor, px: '12px', py: '6px' }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {insight.severity} {t('fleet_insight')}
          </Typography>
        </Box>
        <Box sx={{ p: '14px 16px' }}>
          <Typography
            sx={{
              fontSize: '30px',
              fontWeight: 800,
              color: severityColor,
              lineHeight: 1,
              mb: '4px',
            }}
          >
            {insight.stat}
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: textPrimary,
              mb: '6px',
            }}
          >
            {insight.headline}
          </Typography>
          <Typography
            sx={{ fontSize: '12px', color: textSecondary, lineHeight: 1.6 }}
          >
            {insight.detail}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '6px' }}>
        {LOADING_INSIGHTS.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: i === insightIndex ? '18px' : '6px',
              height: '6px',
              borderRadius: '3px',
              background:
                i === insightIndex
                  ? accent
                  : enableDarkTheme
                  ? 'rgba(255,255,255,0.15)'
                  : '#CBD2DC',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Truck Card ───────────────────────────────────────────────────────────────

function TruckCard({
  truck,
  lastHours,
  enableDarkTheme,
}: {
  truck: HaulTruckData;
  lastHours: number;
  enableDarkTheme: boolean;
}) {
  const { t } = useTranslation();
  const textPrimary = enableDarkTheme ? '#e8ecf4' : '#1a2035';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';
  const trackBg = enableDarkTheme ? 'rgba(255,255,255,0.08)' : '#EEF0F4';
  const cardBg = enableDarkTheme ? '#111c2d' : '#ffffff';
  const border = enableDarkTheme
    ? '0.5px solid #1A3050'
    : '0.5px solid #E0E4EC';
  const { statusBg, statusColor, barColor } = getVerdictStyle(
    truck.verdict,
    enableDarkTheme
  );

  const earlyW = Math.min(
    100,
    Math.round((truck.early_period.avg_tons_per_hour / MAX_TPH) * 100)
  );
  const lateW = Math.min(
    100,
    Math.round((truck.late_period.avg_tons_per_hour / MAX_TPH) * 100)
  );

  const verdictLabel =
    truck.verdict === 'slacking'
      ? `dropped ${truck.decline_pct}% in last ${lastHours} hrs`
      : truck.verdict === 'overperforming'
      ? `picked up in last ${lastHours} hrs`
      : 'no activity all shift';

  return (
    <Box
      sx={{
        background: cardBg,
        border,
        borderRadius: '10px',
        p: '14px 16px',
        mb: '10px',
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          mb: '12px',
        }}
      >
        <Box>
          <Typography
            component="span"
            sx={{ fontSize: '15px', fontWeight: 500, color: textPrimary }}
          >
            {truck.bumper_id}
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: '12px', color: textSecondary, ml: '8px' }}
          >
            {truck.make} {truck.model}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: textSecondary }}>
            {truck.daily_total.production_tons.toLocaleString()}{' '}
            {t('t_nbsp_nbsp')} {truck.daily_total.cycle_count} cycles
          </Typography>
          <Box
            sx={{
              fontSize: '11px',
              fontWeight: 500,
              px: '10px',
              py: '2px',
              borderRadius: '4px',
              background: statusBg,
              color: statusColor,
            }}
          >
            {verdictLabel}
          </Box>
        </Box>
      </Box>

      {/* Early shift bar */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '6px' }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            color: textSecondary,
            width: '70px',
            flexShrink: 0,
          }}
        >
          {t('early_shift')}
        </Typography>
        <Box
          sx={{
            flex: 1,
            background: trackBg,
            borderRadius: '4px',
            height: '10px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${earlyW}%`,
              background: '#1D9E75',
              height: '10px',
              borderRadius: '4px',
              transition: 'width 0.5s ease',
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: textPrimary,
            width: '60px',
            textAlign: 'right',
          }}
        >
          {truck.early_period.avg_tons_per_hour.toFixed(1)} t/hr
        </Typography>
      </Box>

      {/* Late period bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Typography
          sx={{
            fontSize: '12px',
            color: textSecondary,
            width: '70px',
            flexShrink: 0,
          }}
        >
          Last {lastHours} hrs
        </Typography>
        <Box
          sx={{
            flex: 1,
            background: trackBg,
            borderRadius: '4px',
            height: '10px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${lateW}%`,
              background: barColor,
              height: '10px',
              borderRadius: '4px',
              transition: 'width 0.5s ease',
            }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: statusColor,
            width: '60px',
            textAlign: 'right',
          }}
        >
          {truck.late_period.avg_tons_per_hour.toFixed(1)} t/hr
        </Typography>
      </Box>

      {/* Narrative — always visible */}
      <Box
        sx={{
          mt: '12px',
          p: '12px 14px',
          background: statusBg,
          borderRadius: '8px',
        }}
      >
        <Typography
          sx={{ fontSize: '13px', color: textPrimary, lineHeight: 1.7, m: 0 }}
        >
          {truck.narrative}
        </Typography>
      </Box>

      <Box sx={{ mt: '8px', display: 'flex', justifyContent: 'flex-end' }}>
        <FeedbackWidget
          featureName="productivityAIAnalysis"
          metadata={{
            context: 'lastHoursAnalysis_asset',
            bumper_id: truck.bumper_id,
            device: truck.device,
            make: truck.make,
            model: truck.model,
            verdict: truck.verdict,
            decline_pct: String(truck.decline_pct),
            early_tph: String(truck.early_period.avg_tons_per_hour),
            late_tph: String(truck.late_period.avg_tons_per_hour),
            summary: truck.narrative,
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  enableDarkTheme: boolean;
  date: string;
  lastHours?: number;
}

const ProductivityLastHoursModal = ({
  open,
  onClose,
  enableDarkTheme,
  date,
  lastHours = 4,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<LastHoursData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // active params — can be changed via inline editor without closing the modal
  const [activeDate, setActiveDate] = useState(date);
  const [activeHours, setActiveHours] = useState(lastHours);

  // inline editor popover
  const [editAnchor, setEditAnchor] = useState<HTMLElement | null>(null);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const [draftDate, setDraftDate] = useState<Dayjs>(dayjs(date));
  const [draftHours, setDraftHours] = useState(lastHours);

  const api = new AiAnalyticsAPI();

  const fetchAnalysis = useCallback(
    async (fetchDate: string, fetchHours: number) => {
      setIsLoading(true);
      setError(null);
      setData(null);
      try {
        const result = await api.getProductivityLastHoursAnalysis(
          fetchDate,
          fetchHours
        );
        setData(result as LastHoursData);
      } catch {
        setError('Failed to load analysis. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open) {
      setActiveDate(date);
      setActiveHours(lastHours);
      setDraftDate(dayjs(date));
      setDraftHours(lastHours);
      void fetchAnalysis(date, lastHours);
    }
  }, [open]);

  const handleApplyEdit = () => {
    const newDate = draftDate.format('YYYY-MM-DD');
    setActiveDate(newDate);
    setActiveHours(draftHours);
    setEditAnchor(null);
    void fetchAnalysis(newDate, draftHours);
  };

  const bg = enableDarkTheme ? '#0d1f35' : '#F8FAFB';
  const headerBg = enableDarkTheme ? '#0a1929' : '#FFFFFF';
  const borderColor = enableDarkTheme ? '#1A3050' : '#E0E4EC';
  const textPrimary = enableDarkTheme ? '#fff' : '#212121';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';
  const cellBg = enableDarkTheme ? 'rgba(255,255,255,0.05)' : '#F5F7FA';
  const accent = '#5E85F0';

  const totalProduction =
    data?.haul_trucks.reduce(
      (sum, t) => sum + t.daily_total.production_tons,
      0
    ) ?? 0;

  const formattedDate = new Date(activeDate + 'T00:00:00').toLocaleDateString(
    'en-US',
    { month: 'short', day: 'numeric', year: 'numeric' }
  );

  return (
    <CustomMuiThemeProvider>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(860px, 96vw)',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: headerBg,
              borderBottom: `1px solid ${borderColor}`,
              px: '20px',
              py: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Typography
                sx={{ fontSize: '16px', fontWeight: 600, color: textPrimary }}
              >
                Last {activeHours} {t('hours_analysis')}
              </Typography>

              {/* Date + hours badge — click to edit */}
              <Box
                ref={editBtnRef}
                onClick={(e) => {
                  setDraftDate(dayjs(activeDate));
                  setDraftHours(activeHours);
                  setEditAnchor(e.currentTarget);
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '11px',
                  fontWeight: 500,
                  px: '10px',
                  py: '3px',
                  borderRadius: '20px',
                  border: '0.5px solid #AFA9EC',
                  background: '#EEEDFE',
                  color: '#3C3489',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'background 0.15s',
                  '&:hover': { background: '#E0DEFF' },
                }}
              >
                {formattedDate}
                <CalendarMonthIcon sx={{ fontSize: '12px', opacity: 0.7 }} />
              </Box>

              {/* Inline edit popover */}
              <Popover
                open={Boolean(editAnchor)}
                anchorEl={editAnchor}
                onClose={() => {
                  setEditAnchor(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                  sx: {
                    background: enableDarkTheme ? '#0a1929' : '#fff',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '10px',
                    p: '16px',
                    width: '280px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                    mt: '6px',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: textSecondary,
                    mb: '8px',
                  }}
                >
                  {t('date')}
                </Typography>
                <CustomDatePicker
                  value={draftDate}
                  onChange={(val) => {
                    if (val) setDraftDate(val);
                  }}
                  disableFuture
                  maxDate={dayjs().subtract(1, 'day')}
                  sx={{ width: '100%', mb: '14px' }}
                />

                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: textSecondary,
                    mt: '14px',
                    mb: '8px',
                  }}
                >
                  {t('hours_to_analyze')}
                </Typography>
                <Box sx={{ display: 'flex', gap: '6px' }}>
                  {[2, 3, 4, 5, 6].map((h) => (
                    <Box
                      key={h}
                      onClick={() => {
                        setDraftHours(h);
                      }}
                      sx={{
                        flex: 1,
                        height: '34px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                        border:
                          draftHours === h
                            ? '1.5px solid #5E85F0'
                            : `1px solid ${
                                enableDarkTheme ? '#2A3A52' : '#E0E4EC'
                              }`,
                        background:
                          draftHours === h
                            ? enableDarkTheme
                              ? 'rgba(94,133,240,0.18)'
                              : '#EEF3FF'
                            : 'transparent',
                        color:
                          draftHours === h
                            ? '#5E85F0'
                            : enableDarkTheme
                            ? '#8A92A0'
                            : '#616161',
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: '#5E85F0', color: '#5E85F0' },
                      }}
                    >
                      {h}h
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    mt: '16px',
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      setEditAnchor(null);
                    }}
                    sx={{ color: textSecondary, fontSize: '12px' }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleApplyEdit}
                    sx={{
                      fontSize: '12px',
                      background:
                        'linear-gradient(135deg, #4a6fe8 0%, #6b8ef5 100%)',
                      boxShadow: '0 2px 8px rgba(94,133,240,0.4)',
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #3d60d4 0%, #5a7eec 100%)',
                      },
                    }}
                  >
                    {t('apply')}
                  </Button>
                </Box>
              </Popover>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: textSecondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box
            sx={{
              background: bg,
              overflowY: 'auto',
              flex: 1,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                background: enableDarkTheme ? '#2A3A52' : '#CBD0D8',
                borderRadius: '3px',
              },
            }}
          >
            {isLoading ? (
              <LoadingScreen enableDarkTheme={enableDarkTheme} />
            ) : error ? (
              <Box sx={{ p: '40px', textAlign: 'center' }}>
                <Typography sx={{ color: '#E24B4A', fontSize: '14px' }}>
                  {error}
                </Typography>
              </Box>
            ) : data ? (
              <Box sx={{ p: '20px' }}>
                {/* Summary cards */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '10px',
                    mb: '20px',
                  }}
                >
                  <Box
                    sx={{
                      background: cellBg,
                      borderRadius: '8px',
                      p: '12px 16px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', color: textSecondary, mb: '4px' }}
                    >
                      {t('date')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: textPrimary,
                      }}
                    >
                      {formattedDate}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: cellBg,
                      borderRadius: '8px',
                      p: '12px 16px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', color: textSecondary, mb: '4px' }}
                    >
                      {t('total_trucks')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '22px',
                        fontWeight: 500,
                        color: textPrimary,
                      }}
                    >
                      {data.haul_trucks.length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: cellBg,
                      borderRadius: '8px',
                      p: '12px 16px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', color: textSecondary, mb: '4px' }}
                    >
                      {t('analysis_window')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: textPrimary,
                      }}
                    >
                      Last {data.last_hours_analyzed} hrs
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: cellBg,
                      borderRadius: '8px',
                      p: '12px 16px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', color: textSecondary, mb: '4px' }}
                    >
                      {t('total_production')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '22px',
                        fontWeight: 500,
                        color: textPrimary,
                      }}
                    >
                      {totalProduction.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}{' '}
                      t
                    </Typography>
                  </Box>
                </Box>

                {/* Fleet summary */}
                <Box
                  sx={{
                    p: '12px 16px',
                    background: enableDarkTheme
                      ? 'rgba(94,133,240,0.1)'
                      : '#EEF3FF',
                    borderRadius: '8px',
                    mb: '20px',
                    borderLeft: `3px solid ${accent}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: textPrimary,
                      lineHeight: 1.6,
                    }}
                  >
                    {data.fleet_summary.haul_trucks_summary}
                  </Typography>
                  <Box
                    sx={{
                      mt: '8px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <FeedbackWidget
                      featureName="productivityAIAnalysis"
                      metadata={{
                        context: 'lastHoursAnalysis_fleet',
                        date: data.date,
                        last_hours_analyzed: String(data.last_hours_analyzed),
                        total_trucks: String(data.haul_trucks.length),
                        summary: data.fleet_summary.haul_trucks_summary,
                      }}
                    />
                  </Box>
                </Box>

                {/* Legend */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    mb: '12px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        background: '#1D9E75',
                        borderRadius: '2px',
                      }}
                    />
                    <Typography sx={{ fontSize: '12px', color: textSecondary }}>
                      {t('early_shift_avg_t_hr')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      ml: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        background: '#E24B4A',
                        borderRadius: '2px',
                      }}
                    />
                    <Typography sx={{ fontSize: '12px', color: textSecondary }}>
                      Last {data.last_hours_analyzed} {t('hrs_avg_t_hr')}
                    </Typography>
                  </Box>
                </Box>

                {/* Truck cards — sorted by max t/hr descending */}
                {[...data.haul_trucks]
                  .sort(
                    (a, b) =>
                      Math.max(
                        b.early_period.avg_tons_per_hour,
                        b.late_period.avg_tons_per_hour
                      ) -
                      Math.max(
                        a.early_period.avg_tons_per_hour,
                        a.late_period.avg_tons_per_hour
                      )
                  )
                  .map((truck) => (
                    <TruckCard
                      key={truck.device}
                      truck={truck}
                      lastHours={data.last_hours_analyzed}
                      enableDarkTheme={enableDarkTheme}
                    />
                  ))}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Modal>
    </CustomMuiThemeProvider>
  );
};

export default ProductivityLastHoursModal;
